'use client'

import { usePolledData } from '@/hooks/usePolledData'
import { Link } from '@/i18n/navigation'
import { fetchBreakingNews, type NewsItem } from '@/services/news'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'

function formatTime(isoDate: string, lang: string): string {
  const d = new Date(isoDate)
  return d.toLocaleTimeString(lang === 'en' ? 'en-US' : 'tr-TR', { hour: '2-digit', minute: '2-digit' })
}

function formatDate(isoDate: string, lang: string): string {
  const d = new Date(isoDate)
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { day: '2-digit', month: 'short' })
}

function formatCount(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return String(n)
}

const categoryI18n: Record<string, string> = {
  piyasalar: 'news.markets',
  ekonomi: 'news.economy',
  bilancolar: 'news.earnings',
  dunya: 'news.world',
  enerji: 'news.energy',
  teknoloji: 'news.tech',
  kripto: 'news.crypto',
  'forex-emtia': 'news.forexCommodity',
}

function NewsCard({ item, lang }: { item: NewsItem; lang: string }) {
  const catKey = categoryI18n[item.category]
  const catLabel = catKey ? t(catKey, lang) : item.category
  const inner = (
    <>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: 'var(--accent-color)' }}>
          {lang === 'en' ? 'BREAKING' : 'SON DAKİKA'}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
          {item.source}
        </span>
      </div>
      <h3 className="text-[14px] font-bold leading-snug m-0 line-clamp-2" style={{ color: 'var(--text-main)' }}>
        {item.title}
      </h3>
      <p className="text-[12px] mt-1 m-0 line-clamp-2 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        {item.summary}
      </p>
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
          {formatTime(item.publishedAt, lang)} — {formatDate(item.publishedAt, lang)}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--accent-color)' }}>
          {catLabel}
        </span>
        <div className="flex items-center gap-2.5 ml-auto" style={{ color: 'var(--text-muted)' }}>
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            <span className="text-[10px] tabular-nums">{formatCount(item.views)}</span>
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
            <span className="text-[10px] tabular-nums">{formatCount(item.likes)}</span>
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/></svg>
            <span className="text-[10px] tabular-nums">{formatCount(item.dislikes)}</span>
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            <span className="text-[10px] tabular-nums">{formatCount(item.commentCount)}</span>
          </span>
        </div>
      </div>
    </>
  )

  return (
    <Link
      href={{ pathname: '/news/[slug]', params: { slug: item.slug || item.id } }}
      title={item.title}
      className="flex gap-3 py-3 border-b no-underline transition-colors duration-150 hover:bg-[var(--bg-secondary)]"
      style={{ borderColor: 'var(--border-color)' }}
    >
      {item.imageUrl && (
        <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
          {/* TODO: next/image — backend image URL'leri için next.config.ts remotePatterns ekle */}
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">{inner}</div>
    </Link>
  )
}

export default function FeaturedNews({ initialNews }: { initialNews?: NewsItem[] }) {
  const lang = useLocale()
  const { data } = usePolledData(
    () => fetchBreakingNews(lang),
    initialNews && initialNews.length ? initialNews : undefined,
    5 * 60_000,
  )
  const news = data ?? []

  if (news.length === 0) {
    return (
      <section className="mt-2 px-2 md:px-0" style={{ minHeight: 400, contain: 'layout style' }}>
        <h2 className="text-base md:text-xl font-extrabold m-0 mb-3 tracking-tight" style={{ color: 'var(--text-main)' }}>
          {t('featured.breaking', lang)}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="animate-pulse rounded h-3 w-16" style={{ backgroundColor: 'var(--border-color)' }} />
                <div className="animate-pulse rounded h-3 w-12" style={{ backgroundColor: 'var(--border-color)' }} />
              </div>
              <div className="animate-pulse rounded h-4 w-full mb-1" style={{ backgroundColor: 'var(--border-color)' }} />
              <div className="animate-pulse rounded h-4 w-3/4 mb-1" style={{ backgroundColor: 'var(--border-color)' }} />
              <div className="animate-pulse rounded h-3 w-full" style={{ backgroundColor: 'var(--border-color)' }} />
              <div className="animate-pulse rounded h-3 w-24 mt-1.5" style={{ backgroundColor: 'var(--border-color)' }} />
            </div>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="mt-2 px-2 md:px-0">
      <h2 className="text-base md:text-xl font-extrabold m-0 mb-3 tracking-tight" style={{ color: 'var(--text-main)' }}>
        {t('featured.breaking', lang)}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        {news.slice(0, 6).map((item) => (
          <NewsCard key={item.id} item={item} lang={lang} />
        ))}
      </div>

      {news.length > 0 && (
        <Link
          href="/news"
          title={t('featured.viewAll', lang)}
          className="block mt-4 text-center text-[13px] font-semibold no-underline py-3 rounded-lg transition-all duration-200 hover:bg-[var(--accent-color)] hover:text-white"
          style={{ color: 'var(--accent-color)' }}
        >
          {t('featured.viewAll', lang)}
        </Link>
      )}
    </section>
  )
}
